import { AxiosResponse } from 'axios'
import dayjs, { Dayjs } from 'dayjs'

import { _axios } from 'hooks/axios'
import { Shift } from 'types/shift'
import { ShiftManagement } from 'types/shiftManagement'

const FILTER_BY_DATE = `/shifts`
const FILTER_MANAGEMENT = `/shifts/managements`
const SUBMIT = `/shifts/managements/submit`
const SAVE = `/shifts/managements/save`
const BULK_UPSERT = `/shifts/bulk-upsert`

export const shiftApi = () => {
  const findByDate = async (
    start: Dayjs,
    end: Dayjs,
  ): Promise<
    AxiosResponse<{ shifts: Shift[]; shiftChangeDeadlineDays: number }>
  > => {
    const params = {
      startDate: start.format('YYYY-MM-DD'),
      endDate: end.format('YYYY-MM-DD'),
    }
    return await _axios()
      .get(FILTER_BY_DATE, { params })
      .then((res) => {
        return res
      })
  }

  const filterManagement = async (): Promise<
    AxiosResponse<{
      shiftManagements: ShiftManagement[]
      paidLeaveCount: number
    }>
  > => {
    const res = await _axios().get(FILTER_MANAGEMENT)
    const { shiftManagements, paidLeaveCount } = res.data

    // 日付フィールドを dayjs に変換
    const mapped = shiftManagements.map((m: ShiftManagement) => ({
      ...m,
      fromDate: dayjs(m.fromDate),
      toDate: dayjs(m.toDate),
      scheduledRequestDate: dayjs(m.scheduledRequestDate),
      responses: m.responses.map((r) => ({
        ...r,
        date: dayjs(r.date),
      })),
    }))

    return {
      ...res,
      data: {
        shiftManagements: mapped,
        paidLeaveCount,
      },
    }
  }

  const submit = async (
    values: {
      date: Dayjs
      schedule: number
    }[],
  ): Promise<AxiosResponse<Shift>> => {
    const params = {
      values: values.map((v) => ({
        date: v.date.format('YYYY-MM-DD'),
        schedule: v.schedule,
      })),
    }
    return await _axios()
      .post(SUBMIT, params)
      .then((res) => {
        res.data = res.data
        return res
      })
  }

  const save = async (
    shiftManagementId: string,
    date: Dayjs,
    schedule: number,
  ): Promise<AxiosResponse<any>> => {
    const params = {
      shiftManagementId: shiftManagementId,
      date: date.format('YYYY-MM-DD'),
      schedule: schedule,
    }
    return await _axios()
      .post(SAVE, params)
      .then((res) => {
        res.data = res.data
        return res
      })
  }

  const getMonthlyShifts = async (
    yearMonth: string, // YYYY-MM形式
  ): Promise<{ shifts: Shift[]; shiftChangeDeadlineDays: number }> => {
    const date = dayjs(yearMonth, 'YYYY-MM')
    const startDate = date.startOf('month')
    const endDate = date.endOf('month')
    const res = await findByDate(startDate, endDate)
    return res.data || { shifts: [], shiftChangeDeadlineDays: 7 }
  }

  const bulkUpsert = async (params: {
    values: {
      date: string
      schedule: number
    }[]
  }): Promise<AxiosResponse<any>> => {
    return await _axios()
      .post(BULK_UPSERT, params)
      .then((res) => {
        res.data = res.data
        return res
      })
  }

  return {
    findByDate,
    filterManagement,
    submit,
    save,
    getMonthlyShifts,
    bulkUpsert,
  }
}
